import { PreventAndRedirectCommands, RedirectResult, Router, RouterLocation } from "@vaadin/router";
import { Chart, registerables } from "chart.js";
import { endOfMonth, format, startOfMonth, startOfTomorrow, startOfYear, sub } from "date-fns";
import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { SortOrder } from "se-shared/enums/sort-order";
import { ToasterService } from "se-shared/services/toaster.service";
import { container } from "tsyringe";
import { AuthService } from "../../services/auth.service";
import { UsageService } from "../../services/usage.service";
import { UserState } from "../../services/user.state";
import { DataGridColumn } from "../components/data-grid-template";
import { SeDataGrid } from "../components/data-grid.element";
import { SePaginationElement } from "../components/pagination.element";
import { FormSelectEditorElement } from "../editors/form/form-select-editor.element";
import { FormToggleEditorElement } from "../editors/form/form-toggle-editor.element";

Chart.register(...registerables);

@customElement("se-admin-usage")
export class SeAdminUsageElement extends LitElement {
    private _authService: AuthService;
    private _usageService: UsageService;
    private _userState: UserState;
    private _toasterService: ToasterService;
    private _columns: DataGridColumn[] = [];

    private _pageIndex = 1;
    private _recordsPerPage = 10;
    private _totalRecordCount: number;
    private _sortColumn = "name";
    private _sortOrder = 0;

    @state() private _usageDateRangeValue = "1d";
    @state() private _usageTimeUnit = "day";
    private _data = [];
    @state() private _usageTypeCosts: any[] = [];
    @state() private _hideAdminOrgs = true;
    @state() private _hasData = false;

    @query("#chartCanvas") private _chartCanvas: HTMLCanvasElement;
    @query("#usageDateRange") private _usageDateRange: FormSelectEditorElement;
    @query("se-pagination") private _pagination: SePaginationElement;
    @query("se-data-grid") private _dataGrid: SeDataGrid;
    @query("#hideAdminOrgs") private _hideAdminOrgsCheckbox: FormToggleEditorElement;

    private chart?: Chart;
    private labels: string[] = [];
    private datasets: any = [];
    private totalCost = 0.0;
    private totalBaseCost = 0.0;
    private totalProfit = 0.0;
    private startDate = startOfMonth(new Date());
    private endDate = startOfTomorrow();

    private _usageTypeColumns: DataGridColumn[] = [];
    private _usageTypeSortColumn = "type";
    private _usageTypeSortOrder = 0;

    // UI constants
    private RUN_USAGE = "Server Time";
    private EXPORT_DATA_BANDWIDTH = "Export GB";
    private EXPORT_DATA_CPM = "Export CPM";
    private INPUT_COUNT = "Agent Inputs";
    private PROXY_USAGE = "Proxy Data";

    private _paginationInitialized = false;
    private _sortInitialized = false;
    private _initialLoadCompleted = false;

    constructor() {
        super();
        this._authService = container.resolve(AuthService);
        this._toasterService = container.resolve(ToasterService);
        this._userState = container.resolve(UserState);
        this._usageService = container.resolve(UsageService);
    }

    private formatCurrency(value: number, decimalPlaces: number = 2): string {
        return "$" + value.toLocaleString('en-US', { 
            minimumFractionDigits: decimalPlaces, 
            maximumFractionDigits: decimalPlaces 
        });
    }

    connectedCallback() {
        super.connectedCallback();
        document.title = "Admin Usage";

        // Listen for URL initialization events from pagination and data grid
        this.addEventListener('url-initialized', this._handlePaginationInitialized);
        this.addEventListener('sort-initialized', this._handleSortInitialized);
        

        this._userState.selectedSpaceId = -7;
        this._userState.selectedLabelId = -1;
        this._userState.selectedSpaceOrLabelChanged.triggerVoid();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('url-initialized', this._handlePaginationInitialized);
        this.removeEventListener('sort-initialized', this._handleSortInitialized);
    }
    private _handlePaginationInitialized = async (event: CustomEvent) => {
        this._paginationInitialized = true;
        
        // Update page index from the message
        if (event.detail && event.detail.pageIndex) {
            this._pageIndex = event.detail.pageIndex;
        }
        
        await this._tryLoadData();
    }
    
    private _handleSortInitialized = async (event: CustomEvent) => {
        this._sortInitialized = true;
        
        // Update local sort variables from the message
        if (event.detail) {
            const { sortColumn, sortOrder } = event.detail;
            if (sortColumn) {
                this._sortColumn = sortColumn;
                this._sortOrder = sortOrder;
            }
        }
        
        await this._tryLoadData();
    }
    
    private async _tryLoadData() {
        // Load data when both components have initialized
        if (this._paginationInitialized && this._sortInitialized && !this._initialLoadCompleted) {
            await this.loadData();
            this._initialLoadCompleted = true;
        }
    }


    public onBeforeEnter(
        location: RouterLocation,
        commands: PreventAndRedirectCommands,
        router: Router
    ): Promise<unknown> | RedirectResult | undefined {
        if (!this._authService.isSE4Admin) {
            return commands.redirect("/login");
        }
    }

    async firstUpdated() {
        this._usageDateRangeValue = this.getCookie("adminUsageDateRangeValue") || "1d";
        await this.updateDateRange(false);
    }

    private setCookie(name, value, minutes) {
        const now = new Date();
        now.setTime(now.getTime() + minutes * 60 * 1000);
        const expires = "expires=" + now.toUTCString();
        document.cookie = name + "=" + value + ";" + expires + ";path=/";
    }

    private getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(";");
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == " ") c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }

    private async updateDateRange(loadData = true) {
        const today = new Date();
        switch (this._usageDateRangeValue) {
            case "1d":
                this.startDate = startOfMonth(today);
                this.endDate = startOfTomorrow();
                this._usageTimeUnit = "day";
                break;
            case "1m":
                this.startDate = startOfMonth(sub(today, { months: 1 }));
                this.endDate = endOfMonth(sub(today, { months: 1 }));
                this._usageTimeUnit = "day";
                break;
            case "3m":
                this.startDate = startOfMonth(sub(today, { months: 3 }));
                this.endDate = startOfTomorrow();
                this._usageTimeUnit = "day";
                break;
            case "6m":
                this.startDate = startOfMonth(sub(today, { months: 6 }));
                this.endDate = startOfTomorrow();
                this._usageTimeUnit = "day";
                break;
            case "1y":
                this.startDate = startOfYear(sub(today, { years: 1 }));
                this.endDate = startOfTomorrow();
                this._usageTimeUnit = "year";
                break;
            default:
                this.startDate = startOfMonth(today);
                this.endDate = startOfTomorrow();
                this._usageTimeUnit = "day";
                break;
        }

        this._totalRecordCount = 0;
        this._data = [];
        this.totalCost = 0.0;
                        this.totalBaseCost = 0.0;
        this.totalProfit = 0.0;

        if (loadData) {
            await this.loadData();
        }
    }

    private async loadChartData() {
        try {
            // Get chart data
            const chartResult = await this._usageService.api.getAdminUsageChartDataAsync(
                this._usageTimeUnit,
                format(this.startDate, "yyyy-MM-dd"),
                format(this.endDate, "yyyy-MM-dd"),
                this._hideAdminOrgs
            );

            if (chartResult.isErr) {
                this._toasterService.showUnexpectedError(chartResult.err.message);
                return;
            }

            const chartData = chartResult.value;
            
            if (chartData.labels && chartData.labels.length > 0) {
                this._hasData = true;
                
                // Set chart data
                this.labels = Array.from(chartData.labels);
                this.datasets = chartData.aggregatedDataSets;
                
                this.totalCost = chartData.totalCost;  // Customer payments
                this.totalBaseCost = chartData.totalBaseCost;  // Internal costs 
                this.totalProfit = chartData.totalProfit;
                
                // Calculate usage type totals
                this.calculateUsageTypeTotals();
                
                // Update chart if it exists
                if (this.chart) {
                    this.chart.data.labels = this.labels;
                    this.chart.data.datasets = this.getDataSets();
                    this.chart.update();
                } else {
                    this.loadChart();
                }
            } else {
                this._hasData = false;
                this.totalBaseCost = 0;
                this.totalCost = 0;
                this.totalProfit = 0;
                this._usageTypeCosts = [];
                
                if (this.chart) {
                    this.chart.data.labels = [];
                    this.chart.data.datasets = [];
                    this.chart.update();
                }
            }
            
            this.requestUpdate();
            
        } catch (error) {
            console.error("Error loading organization chart data:", error);
            this._toasterService.showUnexpectedError("Failed to load organization chart data");
        }
    }

    private async loadTableData() {
        try {
            // Get table data
            const tableRequest = {
                startDate: format(this.startDate, "yyyy-MM-dd"),
                endDate: format(this.endDate, "yyyy-MM-dd"),
                filterOutAdminOrgs: this._hideAdminOrgs,
                pageIndex: this._pageIndex,
                recordsPerPage: this._recordsPerPage,
                sortColumn: this._sortColumn,
                sortOrder: this._sortOrder
            };

            const tableResult = await this._usageService.api.getAdminOrgUsageTableAsync(tableRequest);

            if (tableResult.isErr) {
                this._toasterService.showUnexpectedError(tableResult.err.message);
                return;
            }

            const tableData = tableResult.value;
            
            // Set table data
            this._data = tableData.organizationUsages;
            this._totalRecordCount = tableData.totalRecordCount;
            
            // Calculate distribution percentages for each organization
            this.calculateDistributionPercentages();
            
            this.requestUpdate();
            
        } catch (error) {
            console.error("Error loading organization table data:", error);
            this._toasterService.showUnexpectedError("Failed to load organization table data");
        }
    }

    private async loadData() {
        // Load both chart and table data (used for initial load and date range changes)
        await Promise.all([
            this.loadChartData(),
            this.loadTableData()
        ]);
    }

    private getColumns() {
        if (this._columns == null || this._columns.length == 0) {
            this._columns = [
                {
                    field: "name",
                    title: "Organization",
                    sortable: true,
                    template: (row) => html`<a href="javascript:;" 
                        style="text-decoration: none; color: #0000ee;" 
                        @click=${() => this.viewOrgUsageDetails(row)}
                        @mouseover=${(e) => e.target.style.textDecoration = "underline"} 
                        @mouseout=${(e) => e.target.style.textDecoration = "none"}>${row.name}</a>`
                },
                {
                    field: "cost",
                    title: "Cost",
                    sortable: true,
                    align: "right",
                    cellStyle: { textAlign: "right", width: "150px" },
                    template: (row) => html`<span class="numbers" style="font-feature-settings: var(--font-feature-numbers);">${this.formatCurrency(row.cost)}</span>`
                },
                {
                    field: "baseCost",
                    title: "Base Cost",
                    sortable: true,
                    align: "right",
                    cellStyle: { textAlign: "right", width: "150px" },
                    template: (row) => html`<span class="numbers" style="font-feature-settings: var(--font-feature-numbers);">${this.formatCurrency(row.baseCost)}</span>`
                },
                {
                    field: "profit",
                    title: "Profit",
                    sortable: true,
                    align: "right",
                    cellStyle: { textAlign: "right", width: "150px" },
                    template: (row) => html`<span class="numbers" style="font-feature-settings: var(--font-feature-numbers);">${this.formatCurrency(row.profit)}</span>`
                },
                {
                    field: "distribution",
                    title: "Distribution",
                    sortable: true,
                    align: "center",
                    cellStyle: { textAlign: "center", width: "300px" },
                    template: (row) => html`${this.getDistribution(row.distribution)}`
                }
            ];
        }
        return this._columns;
    }

    private getDistribution(value: number): TemplateResult {
        return html` 
            <div style="display: flex; justify-content: flex-start;">
                <div style="display: flex; min-width: 100px; height: 16px; justify-content: flex-start; align-items: center; gap: 10px; border-radius: 8px; background: var(--color-blue-50, #cee1f4); overflow: hidden; flex-shrink: 1;">
                    <span style="height: 100%; width: ${value}%; border-radius: 8px; background-color: var(--color-status-blue);"></span>
                </div>
            </div>`;
    }

    private async viewOrgUsageDetails(row: any) {
        Router.go(`manage/admin/usage/org/${row.id}`);
    }

    private handleDateChange() {
        this._usageDateRangeValue = this._usageDateRange.liveValue;
        this.setCookie("adminUsageDateRangeValue", this._usageDateRange.liveValue, 15);
        this._pageIndex = 1;
        this._dataGrid.pageIndex = this._pageIndex;
        this.updateDateRange();
    }

    private async loadChart() {
        if (!this._chartCanvas || this._chartCanvas.getAttribute("prepared") === "true" || !this._hasData) {
            return;
        }
        
        this._chartCanvas.setAttribute("prepared", "true");
        const ctx = this._chartCanvas.getContext("2d");
        
        if (!ctx) {
            return;
        }

        const data = {
            labels: this.labels,
            datasets: this.getDataSets(),
        };

        const options = {
            maintainAspectRatio: false,
            responsive: true,
            scales: {
                x: {
                    stacked: true,
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: "Cost",
                        color: "#1a1f4b",
                        font: {
                            family: "Helvetica",
                            size: 16,
                            weight: "bold",
                        },
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const datasetLabel = context.dataset.label;
                            const value = this.formatCurrency(context.parsed.y);
                            return `${datasetLabel}: ${value}`;
                        },
                    },
                },
            },
        };

        this.chart = new Chart(ctx, {
            type: "bar",
            data: data,
            options: options,
        });
    }

    private getDataSets() {
        const datasets = [];
        
        if (!this.datasets) {
            return datasets;
        }

        this.datasets.forEach((item) => {
            if (item.label == "RunUsage") {
                datasets.push({
                    label: "Server Time",
                    data: item.data,
                    backgroundColor: ["#3F8CD9"],
                    borderColor: ["#3F8CD9"],
                    borderWidth: 1,
                });
            }

            if (item.label == "ExportDataBandwidth") {
                datasets.push({
                    label: "Export GB",
                    data: item.data,
                    backgroundColor: ["#93bfea"],
                    borderColor: ["#93bfea"],
                    borderWidth: 1,
                });
            }

            if (item.label == "ExportDataCpm") {
                datasets.push({
                    label: "Export CPM",
                    data: item.data,
                    backgroundColor: ["#884CC9"],
                    borderColor: ["#884CC9"],
                    borderWidth: 1,
                });
            }

            if (item.label == "InputCount") {
                datasets.push({
                    label: "Agent Inputs",
                    data: item.data,
                    backgroundColor: ["#4E4EF0"],
                    borderColor: ["#4E4EF0"],
                    borderWidth: 1,
                });
            }

            if (item.label == "TrafficData") {
                datasets.push({
                    label: "Proxy Data",
                    data: item.data,
                    backgroundColor: ["#FC833F"],
                    borderColor: ["#FC833F"],
                    borderWidth: 1,
                });
            }
        });

        return datasets;
    }

    private sortDataGrid(evt: CustomEvent) {
        evt.stopPropagation();
        this._sortColumn = evt.detail.sortColumn;
        this._sortOrder = evt.detail.sortOrder === SortOrder.Ascending ? 1 : 0;
        
        // Only load table data when sorting to avoid unnecessary chart reloading
        if (this._initialLoadCompleted) {
            this.loadTableData();
        }
    }

    private onPageChanged(evt: CustomEvent) {
        evt.stopPropagation();
        this._pageIndex = evt.detail.pageIndex;
        this._dataGrid.pageIndex = this._pageIndex;
        
        // Only load table data when paginating to avoid unnecessary chart reloading
        if (this._initialLoadCompleted) {
            this.loadTableData();
        }
    }

    private onHideAdminOrgsChanged() {
        this._hideAdminOrgs = this._hideAdminOrgsCheckbox.liveValue;
        this._pageIndex = 1;
        this._dataGrid.pageIndex = this._pageIndex;
        this.loadData();
    }

    private calculateDistributionPercentages() {
        // Calculate distribution percentages for each organization based on their cost
        if (this.totalCost > 0) {
            this._data.forEach(org => {
                org.distribution = Math.round((org.cost / this.totalCost) * 100 * 100) / 100; // Round to 2 decimal places
            });
        } else {
            this._data.forEach(org => {
                org.distribution = 0;
            });
        }
    }

    private calculateUsageTypeTotals() {
        if (!this.datasets || this.datasets.length === 0 || this.totalCost === 0) {
            this._usageTypeCosts = [];
            return;
        }

        this._usageTypeCosts = [];
        this.datasets.forEach(dataset => {
            let usageType = "";
            switch (dataset.label) {
                case "RunUsage":
                    usageType = this.RUN_USAGE;
                    break;
                case "ExportDataBandwidth":
                    usageType = this.EXPORT_DATA_BANDWIDTH;
                    break;
                case "ExportDataCpm":
                    usageType = this.EXPORT_DATA_CPM;
                    break;
                case "InputCount":
                    usageType = this.INPUT_COUNT;
                    break;
                case "TrafficData":
                    usageType = this.PROXY_USAGE;
                    break;
            }

            if (usageType && dataset.totalCost) {
                const cost = dataset.totalCost;
                const baseCost = dataset.totalBaseCost || 0;
                const profit = cost - baseCost;
                const distribution = (cost / this.totalCost) * 100;
                
                this._usageTypeCosts.push({
                    type: usageType,
                    cost: cost,
                    baseCost: baseCost,
                    profit: profit,
                    distribution: distribution
                });
            }
        });

        // sort by type
        this._usageTypeCosts.sort((a, b) => {
            return this._usageTypeSortOrder === 0 ? 
                String(a[this._usageTypeSortColumn]).localeCompare(String(b[this._usageTypeSortColumn])) : 
                String(b[this._usageTypeSortColumn]).localeCompare(String(a[this._usageTypeSortColumn]));
        });
    }

    private getColorForUsageType(usageType: string): string {
        switch (usageType) {
            case this.RUN_USAGE:
                return "var(--color-status-blue)";
            case this.EXPORT_DATA_BANDWIDTH:
                return "var(--color-blue-50)";
            case this.EXPORT_DATA_CPM:
                return "#884CC9";
            case this.INPUT_COUNT:
                return "var(--color-purple)";
            case this.PROXY_USAGE:
                return "var(--color-status-orange)";
            default:
                return "#ccc";
        }
    }

    private getUsageTypeColumns() {
        if (this._usageTypeColumns == null || this._usageTypeColumns.length == 0) {
            this._usageTypeColumns = [
                {
                    field: "type",
                    title: "Type",
                    sortable: true,
                    template: (row) => {
                        const color = this.getColorForUsageType(row.type);
                        return html`
                            <div style="display: flex; align-items: center; gap: 10px;">
                                <div style="border-radius: 50%; height: 15px; width: 15px; background-color: ${color};"></div>
                                <span>${row.type}</span>
                            </div>
                        `;
                    }
                },
                {
                    field: "cost",
                    title: "Cost",
                    sortable: true,
                    align: "right",
                    cellStyle: { textAlign: "right", width: "150px" },
                    template: (row) =>
                        html`<span style="font-feature-settings: var(--font-feature-numbers);">${this.formatCurrency(row.cost)}</span>`
                },
                {
                    field: "baseCost",
                    title: "Base Cost",
                    sortable: true,
                    align: "right",
                    cellStyle: { textAlign: "right", width: "150px" },
                    template: (row) =>
                        html`<span style="font-feature-settings: var(--font-feature-numbers);">${this.formatCurrency(row.baseCost)}</span>`
                },
                {
                    field: "profit",
                    title: "Profit",
                    sortable: true,
                    align: "right",
                    cellStyle: { textAlign: "right", width: "150px" },
                    template: (row) =>
                        html`<span style="font-feature-settings: var(--font-feature-numbers);">${this.formatCurrency(row.profit)}</span>`
                },
                {
                    field: "distribution",
                    title: "Distribution",
                    sortable: true,
                    align: "center",
                    cellStyle: { textAlign: "center", width: "300px" },
                    template: (row) => html`${this.getDistribution(row.distribution)}`
                }
            ];
        }
        return this._usageTypeColumns;
    }

    private sortUsageTypeDataGrid(evt: CustomEvent) {
        evt.stopPropagation();
        this._usageTypeSortColumn = evt.detail.sortColumn;
        this._usageTypeSortOrder = evt.detail.sortOrder == SortOrder.Ascending ? 1 : 0;

        this._usageTypeCosts.sort((a, b) => {
            const aValue = a[this._usageTypeSortColumn];
            const bValue = b[this._usageTypeSortColumn];
            
            if (typeof aValue === 'number') {
                return this._usageTypeSortOrder === 0 ? aValue - bValue : bValue - aValue;
            } else {
                return this._usageTypeSortOrder === 0 ? 
                    String(aValue).localeCompare(String(bValue)) : 
                    String(bValue).localeCompare(String(aValue));
            }
        });

        this.requestUpdate();
    }

    render() {
        return html`
            <div class="body">

                <div class="container-content scroll-container">
                    <div class="table-header">
                        <div class="left-header">
                            <se-form-toggle-editor
                                id="hideAdminOrgs"
                                .value=${this._hideAdminOrgs}
                                @editorChanged=${this.onHideAdminOrgsChanged}
                                .hideRow=${true}
                                label="Hide internal organizations"
                            ></se-form-toggle-editor>
                            <div class="divider"></div>
                            <se-form-select-editor class="inputEditor" id="usageDateRange" .value="${this._usageDateRangeValue}" width="180px" name="usageDateRange" label="" labelPosition="top"
                                @valueChanged=${this.handleDateChange}
                                .hideRow=${true}
                                .options=${[
                                    { id: "1d", name: "This Month" },
                                    { id: "1m", name: "Last Month" },
                                    { id: "3m", name: "3 Months" },
                                    { id: "6m", name: "6 Months" },
                                    { id: "1y", name: "1 Year" },
                                ]}>
                            </se-form-select-editor>
                        </div>
                        <div class="right-header">
                        </div>
                    </div>
                    <div class="usage-list">
                        <div style="position: relative; height:30vh; flex:1;">
                            <canvas id="chartCanvas"></canvas>
                        </div>
                        <div class="labelsFilters">
                            <div class="market-item-div"><div class="usage-color" style="background-color: var(--color-purple)"></div><span>Agent Inputs</span></div>
                            <div class="market-item-div"><div class="usage-color" style="background-color: #884CC9"></div><span>Export CPM</span></div>
                            <div class="market-item-div"><div class="usage-color" style="background-color: var(--color-blue-50)"></div><span>Export GB</span></div>
                            <div class="market-item-div"><div class="usage-color" style="background-color: var(--color-status-orange)"></div><span>Proxy Data</span></div>
                            <div class="market-item-div"><div class="usage-color" style="background-color: var(--color-status-blue)"></div><span>Server Time</span></div>
                        </div>
                    </div>
                </div>       

                <div class="stats-summary">
                    <div class="stat-card">
                        <div class="stat-label">Total Cost</div>
                        <div class="stat-value">${this.formatCurrency(this.totalCost)}</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-label">Total Base Cost</div>
                        <div class="stat-value">${this.formatCurrency(this.totalBaseCost)}</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-label">Total Profit</div>
                        <div class="stat-value">${this.formatCurrency(this.totalProfit)}</div>
                    </div>
                </div>


                <div class="container-content usage-type-card">
                    <h2 style="padding: 0 5px;" class="table-header">Usage Totals</h2>
                    <se-data-grid 
                        .rows=${this._usageTypeCosts} 
                        .columns=${this.getUsageTypeColumns()}
                        .sortColumn=${this._usageTypeSortColumn}
                        .sortOrder=${this._usageTypeSortOrder === 1 ? SortOrder.Descending : SortOrder.Ascending}
                        @sortdata=${this.sortUsageTypeDataGrid}
                        placeholder="No usage data available"
                        .syncWithUrl=${false}
                    ></se-data-grid>
                </div>

                <div class="container-content" style="flex: 1; margin-bottom: 5px;">
                    <div class="header">
                        <h2 style="padding: 0 5px;" class="table-header">Organizations</h2>
                    </div>
                    <div class="table-list">
                        <se-data-grid 
                            .selectAllCheckbox=${false} 
                            class="grid" 
                            .rows=${this._data} 
                            .recordsPerPage=${this._recordsPerPage} 
                            .pageIndex=${this._pageIndex} 
                            .columns=${this.getColumns()}
                            .sortColumn=${this._sortColumn}
                            .sortOrder=${this._sortOrder === 0 ? SortOrder.Ascending : this._sortOrder === 1 ? SortOrder.Descending : SortOrder.Ascending}
                            @sortdata=${this.sortDataGrid}
                            placeholder="No usage data available."
                            .syncWithUrl=${true}
                        ></se-data-grid>
                    </div>
                    <se-pagination
                        .recordsPerPage=${this._recordsPerPage}
                        .recordCount=${this._totalRecordCount}
                        @pagechanged=${this.onPageChanged}
                        .dataType=${"Organizations"}
                        .syncWithUrl=${true}
                    ></se-pagination>
                </div>
            </div>
        `;
    }

    static styles = css`
        :host {
            display: block;
            box-sizing: border-box;
            font: var(--font);
            height: 100%;
        }
        .body {
            min-height: 220px;
            height: 100%;
            display: flex;
            flex-direction: column;
            gap: 20px;
        }
        .header {
            margin-left: 5px;
            display: flex;
            align-items: end;
            justify-content: space-between;
            overflow: hidden;
            padding-right: 5px;
            margin-right: -5px;
            padding-bottom: 5px;
            margin-bottom: -5px;
        }
        .left-header {
            display: flex;
            align-items: center;
            gap: 15px;
        }
        .right-header {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
        }
        .divider {
            height: 24px;
            width: 1px;
            background-color: var(--color-gray-2);
            margin: 0 5px;
        }
        .grid {
            flex: 1;
        }
        .stats-summary {
            display: flex;
            justify-content: space-between;
            gap: 20px;
            padding: 0px;
        }
        .stat-card {
            flex: 1;
            background-color: white;
            padding: 15px;
            border-radius: 5px;
            box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 5px 0px;
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        .stat-label {
            font-size: 0.9rem;
            color: var(--color-text-secondary);
            margin-bottom: 5px;
        }
        .stat-value {
            font-size: 1.4rem;
            font-weight: 600;
            font-feature-settings: var(--font-feature-numbers);
        }
        .container-content {
            display: flex;
            flex-direction: column;
            background-color: white;
            box-sizing: border-box;
            border-radius: 5px;
            box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 5px 0px;
            padding: 10px;
        }
        .table-list {
            flex: 1;
            overflow: auto;
            display: flex;
            flex-direction: column;
        }
        .scroll-container {
            min-height: 390px;
            overflow: hidden;
            padding: 10px 10px 20px 10px;
            background-color: white;
            box-sizing: border-box;
            border-radius: 5px 5px;
            box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 5px 0px;
        }
        .table-header {
            width: 100%;
            display: flex;
            justify-content: space-between;
            flex-direction: row;
            align-self: flex-end;
            padding: 25px 10px;
            box-sizing: border-box;
        }
        .usage-list {
            display: flex;
            gap: 20px;
        }
        .labelsFilters {
            display: flex;
            gap: 8px;
            flex-direction: column;
            place-content: center flex-start;
            align-items: center;
            height: 100%;
        }
        .market-item-div {
            padding: 12px;
            width: 200px;
            text-align: center;
            border-radius: 9px;
            border: 1px solid var(--color-gray-2, #d0d3dc);
            display: flex;
            gap: 12px;
        }
        .usage-color {
            border-radius: 50%;
            height: 15px;
            width: 15px;
            align-self: center;
        }
        .usage-type-card {
            padding: 10px;
        }
        .numbers {
            font-feature-settings: var(--font-feature-numbers);
        }
        .checkbox {
            width: 1rem;
            height: 1rem;
        }
        input[type="checkbox"]:checked {
            background-color: var(--color-secondary);
        }
        .inputEditor {
            width: 200px;
        }
    `;
} 